import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ClockIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ArrowDownTrayIcon,
} from '../../icons';
import { useTheme } from '../../contexts/ThemeContext';
import { adminAPI } from '../../services/adminAPI';
import TableColumnToggle from '../../components/layout/TableColumnToggle';

const COLUMN_DEFS = [
  { key: 'job_id', label: 'Job ID' },
  { key: 'court', label: 'Court Name' },
  { key: 'case_ref', label: 'Case Reference' },
  { key: 'languages', label: 'Language' },
  { key: 'triggered', label: 'Triggered On' },
  { key: 'duration', label: 'Audio Duration' },
  { key: 'initiated', label: 'Initiated By' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Actions' },
];

const STATUS_OPTIONS = [
  { id: 'all', label: 'All Statuses' },
  { id: 'completed', label: 'Completed' },
  { id: 'processing', label: 'Processing' },
  { id: 'failed', label: 'Failed' },
];

const DATE_RANGES = [
  { id: 'all', label: 'All Time' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'quarter', label: 'This Quarter' },
];

const STATUS_META = {
  completed: { label: 'Completed', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  processing: { label: 'Processing', pill: 'bg-sky-50 text-sky-700 border-sky-200' },
  failed: { label: 'Failed', pill: 'bg-red-50 text-red-700 border-red-200' },
};

const normalizeStatus = (status) => {
  if (status === 'approved' || status === 'completed' || status === 'complete') return 'completed';
  if (status === 'pending_approval' || status === 'processing' || status === 'pending') return 'processing';
  if (status === 'rejected' || status === 'failed') return 'failed';
  return status || 'completed';
};

const withinDateRange = (iso, range) => {
  if (!iso || range === 'all') return true;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return true;
  const now = Date.now();
  const day = 86400000;
  if (range === 'today') return now - d <= day;
  if (range === 'week') return now - d <= 7 * day;
  if (range === 'month') return now - d <= 30 * day;
  if (range === 'quarter') return now - d <= 90 * day;
  return true;
};

const formatTriggerParts = (iso) => {
  if (!iso) return { date: '—', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: '—', time: '' };
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return { date: `${dd}/${mm}/${yyyy}`, time: `${hh}:${min}` };
};

const KpiCard = ({ icon: Icon, label, value, hint, fill = 'primary' }) => {
  const fillClass = {
    primary: 'md-card-kpi-primary',
    dark: 'md-card-kpi-dark',
    warm: 'md-card-kpi-warm',
    slate: 'md-card-kpi-slate',
  }[fill] || 'md-card-kpi-primary';
  return (
    <div className={`md-card ${fillClass}`}>
      <div className="p-6 flex items-start gap-4 min-h-[132px]">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 bg-white/15 shadow-[inset_2px_2px_6px_rgba(0,0,0,0.2),inset_-2px_-2px_6px_rgba(255,255,255,0.12)]">
          <Icon className="h-7 w-7 text-white" />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-xs font-semibold uppercase tracking-wide mb-1 text-white/70">{label}</p>
          <p className="text-3xl font-bold leading-none mb-1 truncate text-white">{value}</p>
          {hint && <p className="text-xs mb-0 text-white/65">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

const SuperAdminRegeneration = () => {
  const { dark } = useTheme();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [visibleKeys, setVisibleKeys] = useState(COLUMN_DEFS.map((c) => c.key));
  const [filters, setFilters] = useState({
    courts: [],
    language: 'all',
    dateRange: 'all',
    dateFrom: '',
    dateTo: '',
    status: 'all',
    query: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const jobsResp = await adminAPI.getRegenReport({});
      const allJobs = (jobsResp.data?.data || jobsResp.data || []).map((j) => ({
        ...j,
        status: normalizeStatus(j.status),
        court_name: j.court_name || j.tenant_name,
        case_reference: j.case_reference || j.case_number,
        triggered_at: j.triggered_at || j.created_at,
        languages: j.languages || (j.language ? [{ name: j.language, pct: 100 }] : []),
        initiated_by: j.initiated_by || j.requested_by || 'System Auto',
        initiated_role: j.initiated_role ?? null,
      }));
      setJobs(allJobs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const courtOptions = useMemo(
    () => [...new Set(jobs.map((j) => j.court_name).filter(Boolean))],
    [jobs],
  );
  const languageOptions = useMemo(() => {
    const set = new Set();
    jobs.forEach((j) => (j.languages || []).forEach((l) => set.add(l.name)));
    return [...set];
  }, [jobs]);

  const filtered = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    return jobs.filter((j) => {
      const matchesCourt = !filters.courts.length || filters.courts.includes(j.court_name);
      const matchesLang = filters.language === 'all'
        || (j.languages || []).some((l) => l.name === filters.language);
      const matchesStatus = filters.status === 'all' || j.status === filters.status;
      const matchesDate = (() => {
        if (filters.dateFrom || filters.dateTo) {
          const t = new Date(j.triggered_at).getTime();
          if (Number.isNaN(t)) return true;
          if (filters.dateFrom) {
            const from = new Date(`${filters.dateFrom}T00:00:00`).getTime();
            if (t < from) return false;
          }
          if (filters.dateTo) {
            const to = new Date(`${filters.dateTo}T23:59:59`).getTime();
            if (t > to) return false;
          }
          return true;
        }
        return withinDateRange(j.triggered_at, filters.dateRange);
      })();
      const matchesQuery = !q
        || j.id?.toLowerCase().includes(q)
        || j.case_reference?.toLowerCase().includes(q)
        || j.court_name?.toLowerCase().includes(q)
        || j.initiated_by?.toLowerCase().includes(q);
      return matchesCourt && matchesLang && matchesStatus && matchesDate && matchesQuery;
    });
  }, [jobs, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);

  useEffect(() => { setPage(1); }, [filters, perPage]);

  const stats = useMemo(() => ({
    total: jobs.length,
    completed: jobs.filter((j) => j.status === 'completed').length,
    processing: jobs.filter((j) => j.status === 'processing').length,
    failed: jobs.filter((j) => j.status === 'failed').length,
  }), [jobs]);

  const show = (key) => visibleKeys.includes(key);

  const inputCls = `w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900'}`;
  const toolBtn = `inline-flex items-center justify-center h-9 px-3 rounded-lg border text-sm ${
    dark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
  }`;
  const dateCls = `px-3 h-9 py-1.5 text-sm rounded-lg border shrink-0 w-[9.75rem] focus:outline-none focus:ring-2 focus:ring-matdash-primary/40
    ${dark ? 'bg-gray-800 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900'}`;
  const dropdownPanel = 'absolute right-0 top-full pt-1 z-[80] hidden group-hover:block';
  const dropdownInner = `w-64 p-3 rounded-xl border shadow-lg space-y-3 ${
    dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
  }`;

  const clearFilters = () => {
    setFilters({ courts: [], language: 'all', dateRange: 'all', dateFrom: '', dateTo: '', status: 'all', query: '' });
  };

  const toggleCourt = (name) => {
    setFilters((f) => ({
      ...f,
      courts: f.courts.includes(name)
        ? f.courts.filter((c) => c !== name)
        : [...f.courts, name],
    }));
  };

  const exportCsv = () => {
    const headers = ['Job ID', 'Court Name', 'Case Reference', 'Languages', 'Trigger Date', 'Duration', 'Initiated By', 'Status'];
    const rows = filtered.map((r) => [
      r.id,
      r.court_name,
      r.case_reference,
      (r.languages || []).map((l) => `${l.name} (${l.pct}%)`).join('; '),
      (() => {
        const p = formatTriggerParts(r.triggered_at);
        return p.time ? `${p.date} ${p.time}` : p.date;
      })(),
      r.duration,
      r.initiated_role ? `${r.initiated_by} (${r.initiated_role})` : r.initiated_by,
      r.status,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'regeneration-directory.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as CSV');
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'regeneration-directory.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported as JSON');
  };

  const pageNumbers = useMemo(() => {
    const max = Math.min(5, totalPages);
    return Array.from({ length: max }, (_, i) => i + 1);
  }, [totalPages]);

  const activeFilterCount = [
    filters.courts.length > 0,
    filters.language !== 'all',
    filters.dateRange !== 'all',
    Boolean(filters.dateFrom),
    Boolean(filters.dateTo),
    filters.status !== 'all',
  ].filter(Boolean).length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard icon={DocumentTextIcon} fill="primary" label="Total Regenerations" value={stats.total} hint="All recorded jobs" />
        <KpiCard icon={CheckCircleIcon} fill="dark" label="Completed" value={stats.completed} hint="Successfully finished" />
        <KpiCard icon={ClockIcon} fill="warm" label="Processing" value={stats.processing} hint="Currently running" />
        <KpiCard icon={ExclamationTriangleIcon} fill="slate" label="Failed" value={stats.failed} hint="Needs attention" />
      </div>

      <div className={`md-card ${dark ? '!bg-gray-900 !border-gray-800' : ''}`}>
        <div className={`relative z-30 px-4 py-3 border-b flex flex-nowrap items-center gap-2 overflow-x-auto ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <div className="relative flex-1 min-w-[180px] max-w-sm shrink">
            <MagnifyingGlassIcon className="h-4 w-4 text-matdash-muted absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={filters.query}
              onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
              placeholder="Search job ID, case, court..."
              className={`${inputCls} pl-9 h-9 py-1.5`}
            />
          </div>

          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value, dateRange: 'all' }))}
            className={dateCls}
            aria-label="From date"
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value, dateRange: 'all' }))}
            className={dateCls}
            aria-label="To date"
          />

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Filters">
                <FunnelIcon className="h-4 w-4" />
                {activeFilterCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-matdash-primary text-white text-[10px] font-semibold">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <div className={dropdownPanel}>
                <div className={dropdownInner}>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Court</label>
                    <div className={`max-h-28 overflow-y-auto rounded-lg border p-2 space-y-1 ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                      {courtOptions.map((c) => (
                        <label key={c} className={`flex items-center gap-2 text-xs cursor-pointer ${dark ? 'text-gray-200' : 'text-matdash-dark'}`}>
                          <input
                            type="checkbox"
                            checked={filters.courts.includes(c)}
                            onChange={() => toggleCourt(c)}
                            className="rounded border-gray-300 text-matdash-primary"
                          />
                          {c}
                        </label>
                      ))}
                      {!courtOptions.length && <p className="text-xs text-matdash-muted">No courts</p>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Language</label>
                    <select
                      value={filters.language}
                      onChange={(e) => setFilters((f) => ({ ...f, language: e.target.value }))}
                      className={inputCls}
                    >
                      <option value="all">All Languages</option>
                      {languageOptions.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Date Range</label>
                    <select
                      value={filters.dateRange}
                      onChange={(e) => setFilters((f) => ({ ...f, dateRange: e.target.value }))}
                      className={inputCls}
                    >
                      {DATE_RANGES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-matdash-muted">Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                      className={inputCls}
                    >
                      {STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </div>
                  <button type="button" onClick={clearFilters} className="text-xs text-matdash-primary">
                    Clear filters
                  </button>
                </div>
              </div>
            </div>

            <TableColumnToggle
              columns={COLUMN_DEFS}
              visibleKeys={visibleKeys}
              onToggle={(key) => setVisibleKeys((keys) => (
                keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]
              ))}
              label=""
            />

            <div className="relative group">
              <button type="button" className={toolBtn} aria-label="Export">
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span className="ml-1.5 hidden sm:inline">Export</span>
              </button>
              <div className={dropdownPanel}>
                <div className={`w-44 py-1 rounded-xl border shadow-lg ${dark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <button
                    type="button"
                    onClick={exportCsv}
                    className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as CSV
                  </button>
                  <button
                    type="button"
                    onClick={exportJson}
                    className={`w-full text-left px-3 py-2 text-sm ${dark ? 'text-gray-200 hover:bg-gray-800' : 'text-matdash-dark hover:bg-gray-50'}`}
                  >
                    Export as JSON
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`px-4 py-2 border-b text-xs ${dark ? 'border-gray-800 text-gray-400' : 'border-matdash-border text-gray-500'}`}>
          Total regenerations: {filtered.length} · Showing {filtered.length ? (page - 1) * perPage + 1 : 0} to {Math.min(page * perPage, filtered.length)}
        </div>

        <div className="overflow-x-auto">
          <table className="md-table min-w-[1200px] w-full">
            <thead>
              <tr>
                {show('job_id') && <th className="whitespace-nowrap px-4 py-3">Job ID</th>}
                {show('court') && <th className="whitespace-nowrap px-4 py-3">Court Name</th>}
                {show('case_ref') && <th className="whitespace-nowrap px-4 py-3">Case Reference</th>}
                {show('languages') && <th className="whitespace-nowrap px-4 py-3">Language</th>}
                {show('triggered') && <th className="whitespace-nowrap px-4 py-3">Triggered On</th>}
                {show('duration') && <th className="whitespace-nowrap px-4 py-3">Audio Duration</th>}
                {show('initiated') && <th className="whitespace-nowrap px-4 py-3">Initiated By</th>}
                {show('status') && <th className="whitespace-nowrap px-4 py-3">Status</th>}
                {show('actions') && <th className="whitespace-nowrap px-4 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={visibleKeys.length || 1} className="text-center text-matdash-muted py-10">
                    Loading regenerations…
                  </td>
                </tr>
              )}
              {!loading && pageRows.length === 0 && (
                <tr>
                  <td colSpan={visibleKeys.length || 1} className="text-center text-matdash-muted py-10">
                    No regenerations found.
                  </td>
                </tr>
              )}
              {!loading && pageRows.map((row) => {
                const meta = STATUS_META[row.status] || STATUS_META.completed;
                const trigger = formatTriggerParts(row.triggered_at);
                return (
                  <tr key={row.id}>
                    {show('job_id') && (
                      <td className="px-4 py-3 whitespace-nowrap min-w-[130px]">
                        <span className={`text-sm font-semibold font-mono ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                          {row.id}
                        </span>
                      </td>
                    )}
                    {show('court') && (
                      <td className="px-4 py-3 whitespace-nowrap min-w-[220px]">
                        <span className={`text-sm font-medium ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                          {row.court_name}
                        </span>
                      </td>
                    )}
                    {show('case_ref') && (
                      <td className="px-4 py-3 whitespace-nowrap min-w-[200px]">
                        {row.case_reference ? (
                          <span
                            className={`text-sm font-medium ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}
                            title="Case dockets are court-scoped. Platform Super Admin cannot open case detail."
                          >
                            {String(row.case_reference).replace(/^Suit\s*No\.?\s*/i, '')}
                          </span>
                        ) : (
                          <span className="text-matdash-muted">—</span>
                        )}
                      </td>
                    )}
                    {show('languages') && (
                      <td className="px-4 py-3 min-w-[200px]">
                        <div className="flex flex-wrap gap-1.5">
                          {(row.languages || []).map((l) => (
                            <span
                              key={`${row.id}-${l.name}`}
                              className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-matdash-primary-subtle text-matdash-primary whitespace-nowrap"
                            >
                              {l.name} ({l.pct}%)
                            </span>
                          ))}
                        </div>
                      </td>
                    )}
                    {show('triggered') && (
                      <td className="px-4 py-3 whitespace-nowrap min-w-[130px]">
                        <p className={`text-sm font-medium mb-0 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>{trigger.date}</p>
                        {trigger.time && <p className="text-xs text-matdash-muted mb-0">{trigger.time}</p>}
                      </td>
                    )}
                    {show('duration') && (
                      <td className={`px-4 py-3 whitespace-nowrap min-w-[130px] text-sm font-mono ${dark ? 'text-gray-200' : 'text-matdash-dark'}`}>
                        {row.duration || '—'}
                      </td>
                    )}
                    {show('initiated') && (
                      <td className="px-4 py-3 whitespace-nowrap min-w-[180px]">
                        <p className={`text-sm font-medium mb-0 ${dark ? 'text-gray-100' : 'text-matdash-dark'}`}>
                          {row.initiated_by}
                          {row.initiated_role ? (
                            <span className="font-normal text-matdash-muted"> ({row.initiated_role})</span>
                          ) : null}
                        </p>
                      </td>
                    )}
                    {show('status') && (
                      <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${meta.pill}`}>
                          {meta.label}
                        </span>
                      </td>
                    )}
                    {show('actions') && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        {row.tenant_id ? (
                          <button
                            type="button"
                            title="View court registration"
                            aria-label="View court registration"
                            onClick={() => navigate('/super-admin/tenants', {
                              state: { openTenantId: row.tenant_id },
                            })}
                            className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border ${
                              dark
                                ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                                : 'border-gray-200 text-matdash-primary hover:bg-matdash-primary-subtle'
                            }`}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            title="Court unavailable"
                            aria-label="Court unavailable"
                            onClick={() => toast.message('Court registration unavailable', {
                              description: row.court_name || row.id,
                            })}
                            className={`w-8 h-8 inline-flex items-center justify-center rounded-lg border opacity-60 ${
                              dark
                                ? 'border-gray-700 text-gray-300'
                                : 'border-gray-200 text-matdash-primary'
                            }`}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={`px-4 py-3 border-t flex flex-wrap items-center justify-between gap-3 ${dark ? 'border-gray-800' : 'border-matdash-border'}`}>
          <p className="text-xs text-matdash-muted mb-0">
            Showing {filtered.length === 0 ? 0 : (page - 1) * perPage + 1} to {Math.min(page * perPage, filtered.length)} of {filtered.length} results
          </p>
          <div className="flex items-center gap-2">
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className={`h-8 px-2 text-xs rounded-lg border ${dark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200'}`}
            >
              {[10, 25, 50].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">‹</button>
            {pageNumbers.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setPage(n)}
                className={`w-8 h-8 text-xs rounded ${page === n ? 'bg-matdash-primary text-white' : 'border border-matdash-border text-matdash-muted'}`}
              >
                {n}
              </button>
            ))}
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 text-xs rounded border border-matdash-border disabled:opacity-40">›</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminRegeneration;
